import { IMemberDetailsData } from 'fitbeat.gym-core';
import { contentTopicConsumedEvents } from 'fitbeat.models';
import { MonthlyPulseUnit, toPulseValue } from './MonthlyPulseData';

// Product conversion constants for Monthly Pulse exercise fun-facts.
export const AVG_STEP_LENGTH_METERS = 0.762;
export const EARTH_CIRCUMFERENCE_METERS = 40_075_000;
export const MINUTES_PER_DAY = 24 * 60;

export interface IMonthlyPulseAggregateStats {
  gymName: string;
  month: string;
  isPartial: boolean;
  monthStart: string;
  monthEnd: string;
  ytdStart: string;
  memberCount: number;
  memberCountSuffix: MonthlyPulseUnit;
  stepsMonth: number;
  stepsMonthSuffix: MonthlyPulseUnit;
  stepsYtd: number;
  stepsYtdSuffix: MonthlyPulseUnit;
  aroundTheEarthPercent: number;
  aroundTheEarthPercentSuffix: MonthlyPulseUnit;
  totalWorkoutMinutesMonth: number;
  totalWorkoutMinutesMonthSuffix: MonthlyPulseUnit;
  totalWorkoutMinutesYtd: number;
  totalWorkoutMinutesYtdSuffix: MonthlyPulseUnit;
  daysOfMovement: number;
  daysOfMovementSuffix: MonthlyPulseUnit;
  totalCaloriesMonth: number;
  totalCaloriesMonthSuffix: MonthlyPulseUnit;
  totalCaloriesYtd: number;
  totalCaloriesYtdSuffix: MonthlyPulseUnit;
  caloriesBurntBurgersYtd: number;
  caloriesBurntBurgersYtdSuffix: MonthlyPulseUnit;
  caloriesBurntBurgersMonth: number;
  caloriesBurntBurgersMonthSuffix: MonthlyPulseUnit;
  totalWorkoutsMonth: number;
  totalWorkoutsMonthSuffix: MonthlyPulseUnit;
  totalWorkoutsYtd: number;
  totalWorkoutsYtdSuffix: MonthlyPulseUnit;
  strengthMonth: number;
  strengthMonthSuffix: MonthlyPulseUnit;
  strengthYtd: number;
  strengthYtdSuffix: MonthlyPulseUnit;
  cardioMonth: number;
  cardioMonthSuffix: MonthlyPulseUnit;
  cardioYtd: number;
  cardioYtdSuffix: MonthlyPulseUnit;
  mobilityMonth: number;
  mobilityMonthSuffix: MonthlyPulseUnit;
  mobilityYtd: number;
  mobilityYtdSuffix: MonthlyPulseUnit;
  // Donut segments from raw monthly counts (not display-scaled values).
  donutStrengthPct: number;
  donutCardioPct: number;
  donutMobilityPct: number;
  homeWorkoutsMonth: number;
  homeWorkoutsMonthSuffix: MonthlyPulseUnit;
  homeWorkoutsYtd: number;
  homeWorkoutsYtdSuffix: MonthlyPulseUnit;
  studioWorkoutsMonth: number;
  studioWorkoutsMonthSuffix: MonthlyPulseUnit;
  studioWorkoutsYtd: number;
  studioWorkoutsYtdSuffix: MonthlyPulseUnit;
  totalXpMonth: number;
  totalXpMonthSuffix: MonthlyPulseUnit;
  totalXpYtd: number;
  totalXpYtdSuffix: MonthlyPulseUnit;
  featuredChallengeJoinCount: number;
  featuredChallengeJoinCountSuffix: MonthlyPulseUnit;
  featuredChallengeJoinPercent: number;
  featuredChallengeJoinPercentSuffix: MonthlyPulseUnit;
  weeklyGoalReachedCount: number;
  weeklyGoalReachedCountSuffix: MonthlyPulseUnit;
  weeklyGoalReachedPercent: number;
  weeklyGoalReachedPercentSuffix: MonthlyPulseUnit;
  stepsTargetMetCount: number;
  stepsTargetMetCountSuffix: MonthlyPulseUnit;
  stepsTargetMetPercent: number;
  stepsTargetMetPercentSuffix: MonthlyPulseUnit;
  congratulationsSentMonth: number;
  congratulationsSentMonthSuffix: MonthlyPulseUnit;
  congratulationsSentYtd: number;
  congratulationsSentYtdSuffix: MonthlyPulseUnit;
  challengesCompletedMonth: number;
  challengesCompletedMonthSuffix: MonthlyPulseUnit;
  challengesCompletedYtd: number;
  challengesCompletedYtdSuffix: MonthlyPulseUnit;
  interactionsCountMonth: number;
  interactionsCountMonthSuffix: MonthlyPulseUnit;
  interactionsCountYtd: number;
  interactionsCountYtdSuffix: MonthlyPulseUnit;
  readsYtd: number;
  readsYtdSuffix: MonthlyPulseUnit;
  readsMonth: number;
  readsMonthSuffix: MonthlyPulseUnit;
  listensYtd: number;
  listensYtdSuffix: MonthlyPulseUnit;
  listensMonth: number;
  listensMonthSuffix: MonthlyPulseUnit;
  mostInteractive: Array<{
    uid: string;
    displayName: string;
    xpMonth: number;
    xpMonthSuffix: MonthlyPulseUnit;
    xpYtd: number;
    xpYtdSuffix: MonthlyPulseUnit;
  }>;
}

interface IWorkoutFields {
  totalCompleted: number;
  totalCompletedHome: number;
  totalCompletedStudio: number;
  totalBurnCompleted: number;
  totalBuildCompleted: number;
  totalBalanceCompleted: number;
  totalDurationInMinutes: number;
  totalCalories: number;
}

const getWorkoutNum = (agg: Partial<IWorkoutFields> | undefined, key: keyof IWorkoutFields): number =>
  Number((agg as IWorkoutFields | undefined)?.[key] ?? 0);
const getXpNum = (stats: Record<string, number> | undefined, key: string): number =>
  Number(stats?.[key] ?? 0);

// Blog reads are contentTopicConsumed<Topic> events (Podcast excluded).
// articleRead is the legacy pre-1.0.303 event, kept for older data.
const BLOG_CONTENT_TOPIC_EVENTS = contentTopicConsumedEvents.filter(
  (e) => e !== 'contentTopicConsumedPodcast',
);

function sumBlogReads(stats: Record<string, number> | undefined): number {
  let total = getXpNum(stats, 'xpCount-event-articleRead');
  for (const ev of BLOG_CONTENT_TOPIC_EVENTS) {
    total += getXpNum(stats, `xpCount-event-${ev}`);
  }
  return total;
}

export function computeMonthlyPulseAggregates(
  members: IMemberDetailsData[],
  gymName: string,
  month: string,
  monthBounds?: { monthStart: Date; monthEnd: Date; ytdStart: Date; isPartial: boolean },
  featuredJoinerIds?: string[],
): IMonthlyPulseAggregateStats {
  const memberCount = members.length;
  let stepsMonth = 0;
  let stepsYtd = 0;
  let totalWorkoutMinutesMonth = 0;
  let totalWorkoutMinutesYtd = 0;
  let totalCaloriesMonth = 0;
  let totalCaloriesYtd = 0;
  let totalWorkoutsMonth = 0;
  let totalWorkoutsYtd = 0;
  let strengthMonth = 0;
  let strengthYtd = 0;
  let cardioMonth = 0;
  let cardioYtd = 0;
  let mobilityMonth = 0;
  let mobilityYtd = 0;
  let homeMonth = 0;
  let homeYtd = 0;
  let studioMonth = 0;
  let studioYtd = 0;
  let xpMonth = 0;
  let xpYtd = 0;
  let weeklyGoalCount = 0;
  let stepsTargetCount = 0;
  let congratulationsSentMonth = 0;
  let congratulationsSentYtd = 0;
  let challengesCompletedMonth = 0;
  let challengesCompletedYtd = 0;
  let interactionsCountMonth = 0;
  let interactionsCountYtd = 0;
  let readsYtd = 0;
  let readsMonth = 0;
  let listensYtd = 0;
  let listensMonth = 0;
  const interactive: Array<{ uid: string; displayName: string; xpMonth: number; xpYtd: number }> = [];

  for (const singleMember of members) {
    const ytdAgg = (singleMember.monthlyPulse?.ytdWorkoutAggregate ?? {}) as Partial<IWorkoutFields>;
    const ytdXp = (singleMember.monthlyPulse?.ytdXpStats ?? {}) as Record<string, number>;
    const monthAgg = (singleMember.monthlyPulse?.workoutAggregate ?? {}) as Partial<IWorkoutFields>;
    const monthXp = (singleMember.monthlyPulse?.xpStats ?? {}) as Record<string, number>;
    stepsMonth += getXpNum(monthXp, 'stepsTaken');
    stepsYtd += getXpNum(ytdXp, 'stepsTaken');
    totalWorkoutMinutesMonth += getWorkoutNum(monthAgg, 'totalDurationInMinutes');
    totalWorkoutMinutesYtd += getWorkoutNum(ytdAgg, 'totalDurationInMinutes');
    totalCaloriesMonth += getWorkoutNum(monthAgg, 'totalCalories');
    totalCaloriesYtd += getWorkoutNum(ytdAgg, 'totalCalories');
    totalWorkoutsMonth += getWorkoutNum(monthAgg, 'totalCompleted');
    totalWorkoutsYtd += getWorkoutNum(ytdAgg, 'totalCompleted');
    strengthMonth += getWorkoutNum(monthAgg, 'totalBuildCompleted');
    strengthYtd += getWorkoutNum(ytdAgg, 'totalBuildCompleted');
    cardioMonth += getWorkoutNum(monthAgg, 'totalBurnCompleted');
    cardioYtd += getWorkoutNum(ytdAgg, 'totalBurnCompleted');
    mobilityMonth += getWorkoutNum(monthAgg, 'totalBalanceCompleted');
    mobilityYtd += getWorkoutNum(ytdAgg, 'totalBalanceCompleted');
    homeMonth += getWorkoutNum(monthAgg, 'totalCompletedHome');
    homeYtd += getWorkoutNum(ytdAgg, 'totalCompletedHome');
    studioMonth += getWorkoutNum(monthAgg, 'totalCompletedStudio');
    studioYtd += getWorkoutNum(ytdAgg, 'totalCompletedStudio');
    const xpMonthVal = getXpNum(monthXp, 'xpSumPoints-all');
    const xpYtdVal = getXpNum(ytdXp, 'xpSumPoints-all');
    xpMonth += xpMonthVal;
    xpYtd += xpYtdVal;
    if (getXpNum(monthXp, 'xpCount-event-weeklyWorkoutGoalAchieved') > 0) {
      weeklyGoalCount += 1;
    }
    if (getXpNum(monthXp, 'xpCount-event-stepsCompleted') > 0) {
      stepsTargetCount += 1;
    }
    congratulationsSentMonth += getXpNum(monthXp, 'xpCount-event-congratulationsSent');
    congratulationsSentYtd += getXpNum(ytdXp, 'xpCount-event-congratulationsSent');
    challengesCompletedMonth
      += getXpNum(monthXp, 'xpCount-event-challengeCompletedMonthly')
      + getXpNum(monthXp, 'xpCount-event-challengeCompletedWeekly');
    challengesCompletedYtd
      += getXpNum(ytdXp, 'xpCount-event-challengeCompletedMonthly')
      + getXpNum(ytdXp, 'xpCount-event-challengeCompletedWeekly');
    interactionsCountMonth
      += getXpNum(monthXp, 'xpCount-event-groupPostReacted')
      + getXpNum(monthXp, 'xpCount-event-groupPostCommented');
    interactionsCountYtd
      += getXpNum(ytdXp, 'xpCount-event-groupPostReacted')
      + getXpNum(ytdXp, 'xpCount-event-groupPostCommented');
    readsYtd += sumBlogReads(ytdXp);
    readsMonth += sumBlogReads(monthXp);
    listensYtd += getXpNum(ytdXp, 'xpCount-event-contentTopicConsumedPodcast');
    listensMonth += getXpNum(monthXp, 'xpCount-event-contentTopicConsumedPodcast');
    const displayName = (singleMember.userSettings as { displayName?: string })?.displayName
      || singleMember.userSettings?.fullname
      || singleMember.userSettings?.email
      || singleMember.userSettings?.uid
      || '';
    interactive.push({
      uid: singleMember.userSettings.uid,
      displayName,
      xpMonth: xpMonthVal,
      xpYtd: xpYtdVal,
    });
  }

  interactive.sort((a, b) => b.xpMonth - a.xpMonth);
  const mostInteractive = interactive.slice(0, 10);

  const pct = (count: number): number => {
    if (memberCount === 0) {
      return 0;
    }
    return Math.round((count / memberCount) * 1000) / 10;
  };

  const mc = toPulseValue(memberCount);
  const sm = toPulseValue(stepsMonth);
  const sy = toPulseValue(stepsYtd);
  const earthPct = stepsMonth === 0
    ? 0
    : Math.round(((stepsMonth * AVG_STEP_LENGTH_METERS) / EARTH_CIRCUMFERENCE_METERS) * 1000) / 10;
  const ate = toPulseValue(earthPct, true);
  const twmm = toPulseValue(totalWorkoutMinutesMonth);
  const twmy = toPulseValue(totalWorkoutMinutesYtd);
  const daysMove = Math.round((totalWorkoutMinutesMonth / MINUTES_PER_DAY) * 10) / 10;
  const dom = toPulseValue(daysMove);
  const tcm = toPulseValue(totalCaloriesMonth);
  const tcy = toPulseValue(totalCaloriesYtd);
  const twkm = toPulseValue(totalWorkoutsMonth);
  const twky = toPulseValue(totalWorkoutsYtd);
  const stm = toPulseValue(strengthMonth);
  const sty = toPulseValue(strengthYtd);
  const cm = toPulseValue(cardioMonth);
  const cy = toPulseValue(cardioYtd);
  const mm = toPulseValue(mobilityMonth);
  const my = toPulseValue(mobilityYtd);
  const hm = toPulseValue(homeMonth);
  const hy = toPulseValue(homeYtd);
  const stm2 = toPulseValue(studioMonth);
  const sty2 = toPulseValue(studioYtd);
  const txm = toPulseValue(xpMonth);
  const txy = toPulseValue(xpYtd);
  // Numerator and denominator must be the same population: members already
  // excludes admin-settings emails, so intersect joiner ids with them.
  const memberUids = new Set(members.map((m) => m.userSettings?.uid));
  const joinerCount = (featuredJoinerIds ?? []).filter((id) => memberUids.has(id)).length;
  const fjc = toPulseValue(joinerCount);
  const fjp = toPulseValue(pct(joinerCount), true);
  const wgc = toPulseValue(weeklyGoalCount);
  const wgp = toPulseValue(pct(weeklyGoalCount), true);
  const stc = toPulseValue(stepsTargetCount);
  const stp = toPulseValue(pct(stepsTargetCount), true);
  const csm = toPulseValue(congratulationsSentMonth);
  const csy = toPulseValue(congratulationsSentYtd);
  const chcm = toPulseValue(challengesCompletedMonth);
  const chcy = toPulseValue(challengesCompletedYtd);
  const icm = toPulseValue(interactionsCountMonth);
  const icy = toPulseValue(interactionsCountYtd);
  const ry = toPulseValue(readsYtd);
  const rm = toPulseValue(readsMonth);
  const ly = toPulseValue(listensYtd);
  const lm = toPulseValue(listensMonth);
  const cbbYtd = toPulseValue(Math.floor(totalCaloriesYtd / 500));
  const cbbMonth = toPulseValue(Math.floor(totalCaloriesMonth / 500));

  const workoutTypeMonthTotal = strengthMonth + cardioMonth + mobilityMonth;
  const donutPct = (count: number): number => {
    if (workoutTypeMonthTotal <= 0) {
      return 0;
    }
    return Math.round((count / workoutTypeMonthTotal) * 1000) / 10;
  };

  return {
    gymName,
    month,
    isPartial: monthBounds?.isPartial ?? false,
    monthStart: monthBounds?.monthStart?.toISOString() ?? '',
    monthEnd: monthBounds?.monthEnd?.toISOString() ?? '',
    ytdStart: monthBounds?.ytdStart?.toISOString() ?? '',
    memberCount: mc.value,
    memberCountSuffix: mc.suffix,
    stepsMonth: sm.value,
    stepsMonthSuffix: sm.suffix,
    stepsYtd: sy.value,
    stepsYtdSuffix: sy.suffix,
    aroundTheEarthPercent: ate.value,
    aroundTheEarthPercentSuffix: ate.suffix,
    totalWorkoutMinutesMonth: twmm.value,
    totalWorkoutMinutesMonthSuffix: twmm.suffix,
    totalWorkoutMinutesYtd: twmy.value,
    totalWorkoutMinutesYtdSuffix: twmy.suffix,
    daysOfMovement: dom.value,
    daysOfMovementSuffix: dom.suffix,
    totalCaloriesMonth: tcm.value,
    totalCaloriesMonthSuffix: tcm.suffix,
    totalCaloriesYtd: tcy.value,
    totalCaloriesYtdSuffix: tcy.suffix,
    caloriesBurntBurgersYtd: cbbYtd.value,
    caloriesBurntBurgersYtdSuffix: cbbYtd.suffix,
    caloriesBurntBurgersMonth: cbbMonth.value,
    caloriesBurntBurgersMonthSuffix: cbbMonth.suffix,
    totalWorkoutsMonth: twkm.value,
    totalWorkoutsMonthSuffix: twkm.suffix,
    totalWorkoutsYtd: twky.value,
    totalWorkoutsYtdSuffix: twky.suffix,
    strengthMonth: stm.value,
    strengthMonthSuffix: stm.suffix,
    strengthYtd: sty.value,
    strengthYtdSuffix: sty.suffix,
    cardioMonth: cm.value,
    cardioMonthSuffix: cm.suffix,
    cardioYtd: cy.value,
    cardioYtdSuffix: cy.suffix,
    mobilityMonth: mm.value,
    mobilityMonthSuffix: mm.suffix,
    mobilityYtd: my.value,
    mobilityYtdSuffix: my.suffix,
    donutStrengthPct: donutPct(strengthMonth),
    donutCardioPct: donutPct(cardioMonth),
    donutMobilityPct: donutPct(mobilityMonth),
    homeWorkoutsMonth: hm.value,
    homeWorkoutsMonthSuffix: hm.suffix,
    homeWorkoutsYtd: hy.value,
    homeWorkoutsYtdSuffix: hy.suffix,
    studioWorkoutsMonth: stm2.value,
    studioWorkoutsMonthSuffix: stm2.suffix,
    studioWorkoutsYtd: sty2.value,
    studioWorkoutsYtdSuffix: sty2.suffix,
    totalXpMonth: txm.value,
    totalXpMonthSuffix: txm.suffix,
    totalXpYtd: txy.value,
    totalXpYtdSuffix: txy.suffix,
    featuredChallengeJoinCount: fjc.value,
    featuredChallengeJoinCountSuffix: fjc.suffix,
    featuredChallengeJoinPercent: fjp.value,
    featuredChallengeJoinPercentSuffix: fjp.suffix,
    weeklyGoalReachedCount: wgc.value,
    weeklyGoalReachedCountSuffix: wgc.suffix,
    weeklyGoalReachedPercent: wgp.value,
    weeklyGoalReachedPercentSuffix: wgp.suffix,
    stepsTargetMetCount: stc.value,
    stepsTargetMetCountSuffix: stc.suffix,
    stepsTargetMetPercent: stp.value,
    stepsTargetMetPercentSuffix: stp.suffix,
    congratulationsSentMonth: csm.value,
    congratulationsSentMonthSuffix: csm.suffix,
    congratulationsSentYtd: csy.value,
    congratulationsSentYtdSuffix: csy.suffix,
    challengesCompletedMonth: chcm.value,
    challengesCompletedMonthSuffix: chcm.suffix,
    challengesCompletedYtd: chcy.value,
    challengesCompletedYtdSuffix: chcy.suffix,
    interactionsCountMonth: icm.value,
    interactionsCountMonthSuffix: icm.suffix,
    interactionsCountYtd: icy.value,
    interactionsCountYtdSuffix: icy.suffix,
    readsYtd: ry.value,
    readsYtdSuffix: ry.suffix,
    readsMonth: rm.value,
    readsMonthSuffix: rm.suffix,
    listensYtd: ly.value,
    listensYtdSuffix: ly.suffix,
    listensMonth: lm.value,
    listensMonthSuffix: lm.suffix,
    mostInteractive: mostInteractive.map((m) => {
      const xm = toPulseValue(m.xpMonth);
      const xy = toPulseValue(m.xpYtd);
      return {
        uid: m.uid,
        displayName: m.displayName,
        xpMonth: xm.value,
        xpMonthSuffix: xm.suffix,
        xpYtd: xy.value,
        xpYtdSuffix: xy.suffix,
      };
    }),
  };
}
